import { create } from 'zustand';

const CHECK_EVERY_MS = 5 * 60_000;

export const useAppUpdate = create(() => ({ ready: false }));

// Fields the user typed into since the page loaded. A field counts only while it is still on the page inside a
// form, so leaving the screen forgets it (a form without a submit that clears it is treated as still unsaved).
const typed = new Set<Element>();
document.addEventListener('input', (e) => e.target instanceof Element && typed.add(e.target), true);

/** True when a reload would lose work: any open dialog, or typed input inside a form. */
export function hasUnsavedInput(): boolean {
  if (document.querySelector('[role="dialog"]')) return true;
  for (const el of typed) {
    if (!el.isConnected) typed.delete(el);
    else if (el.closest('form')) return true;
  }
  return false;
}

type Register = (options: {
  immediate: boolean;
  onNeedRefresh: () => void;
  onRegisteredSW: (url: string, registration?: ServiceWorkerRegistration) => void;
}) => (reloadPage?: boolean) => Promise<void>;

let activate: ((reloadPage?: boolean) => Promise<void>) | undefined;

/** Tells the waiting worker to take over (skipWaiting); the page reloads when it does. */
export const applyUpdate = () => void activate?.(true);

function autoApplyIfIdle() {
  if (useAppUpdate.getState().ready && document.visibilityState === 'hidden' && !hasUnsavedInput()) {
    applyUpdate();
  }
}

/**
 * registerType 'prompt': a new build waits until applyUpdate(). Checks for one every 5 minutes and when the tab
 * becomes visible. Tabs that are hidden update by themselves, unless something unsaved is on screen.
 */
export function startAppUpdate(register: Register) {
  useAppUpdate.setState({ ready: false });
  activate = register({
    immediate: true,
    onNeedRefresh() {
      useAppUpdate.setState({ ready: true });
      autoApplyIfIdle();
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => void registration.update().catch(() => undefined);
      setInterval(check, CHECK_EVERY_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
        else autoApplyIfIdle();
      });
    },
  });
}
